import { upgradeArt, upgradeCardArt, upgradeCardPosition } from './upgrade_art.js';

const $ = (id) => document.getElementById(id);

let upgradeUi = null;

export function initUpgrades(options = {}) {
  upgradeUi = {
    options,
    root: $('upgradeScreen'),
    cards: $('upgradeCards'),
    title: $('upgradeTitle'),
    subtitle: $('upgradeSub'),
    skipButton: $('skipUpgrade'),
    rerollButton: ensureRerollButton(),
    onPick: null,
    onReroll: null,
    rerollsLeft: 1,
  };
  upgradeUi.skipButton?.addEventListener('click', () => {
    upgradeUi.options.onSkip?.();
    hideUpgradeScreen();
  });
  upgradeUi.rerollButton?.addEventListener('click', () => {
    if (upgradeUi.rerollsLeft <= 0) return;
    upgradeUi.rerollsLeft -= 1;
    const choices = upgradeUi.onReroll?.();
    if (choices?.length) renderUpgradeCards(choices, upgradeUi.state || {});
    updateRerollButton();
  });
  injectUpgradeStyles();
  return upgradeUi;
}

export function showUpgradeScreen(choices = [], options = {}) {
  if (!upgradeUi) initUpgrades();
  upgradeUi.options = { ...upgradeUi.options, ...options };
  upgradeUi.onPick = options.onPick || null;
  upgradeUi.onReroll = options.onReroll || null;
  upgradeUi.rerollsLeft = 1;
  upgradeUi.state = options.state || {};
  if (upgradeUi.title) upgradeUi.title.textContent = options.title || 'Choose an upgrade';
  if (upgradeUi.subtitle) upgradeUi.subtitle.textContent = options.subtitle || 'Pick one card. The build compounds for the rest of this run.';
  updateRerollButton();
  renderUpgradeCards(choices, upgradeUi.state);
  upgradeUi.root?.classList.remove('hidden');
}

export function hideUpgradeScreen() {
  if (!upgradeUi) initUpgrades();
  upgradeUi.root?.classList.add('hidden');
}

export function renderUpgradeCards(choices = [], state = {}) {
  if (!upgradeUi) initUpgrades();
  const cards = upgradeUi.cards;
  if (!cards) return;

  cards.innerHTML = '';
  choices.forEach((choice, index) => {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = `upgrade-card has-art ${choice.rarity || 'common'}`;
    card.style.animationDelay = `${index * 48}ms`;
    card.style.setProperty('--upgrade-art', upgradeArt(choice));
    card.style.setProperty('--upgrade-card-art', upgradeCardArt(choice));
    card.style.setProperty('--upgrade-card-pos', upgradeCardPosition(choice));
    card.innerHTML = [
      '<i class="rarity-ring"></i>',
      '<span class="upgrade-icon art" aria-hidden="true"></span>',
      `<small>${escapeHtml(choice.rarity || 'common')}</small>`,
      `<strong>${escapeHtml(choice.name || 'Upgrade')}</strong>`,
      `<span>${escapeHtml(choice.description || '')}</span>`,
      `<em class="synergy-hint">${escapeHtml(choice.hint || buildHint(choice, state))}</em>`,
    ].join('');
    card.addEventListener('click', () => {
      upgradeUi.onPick?.(choice, index);
      hideUpgradeScreen();
    });
    cards.appendChild(card);
  });
}

function ensureRerollButton() {
  let button = $('rerollUpgrade');
  if (button) return button;
  const actions = document.querySelector('.upgrade-actions');
  button = document.createElement('button');
  button.id = 'rerollUpgrade';
  button.type = 'button';
  button.textContent = 'Reroll (1 left)';
  actions?.prepend(button);
  return button;
}

function updateRerollButton() {
  if (!upgradeUi?.rerollButton) return;
  upgradeUi.rerollButton.textContent = `Reroll (${upgradeUi.rerollsLeft} left)`;
  upgradeUi.rerollButton.disabled = upgradeUi.rerollsLeft <= 0;
}

function injectUpgradeStyles() {
  if ($('phase4UpgradeStyles')) return;
  const style = document.createElement('style');
  style.id = 'phase4UpgradeStyles';
  style.textContent = `
    .upgrade-card {
      overflow:hidden; transform-style:preserve-3d; animation:cardFlip 200ms ease-out both;
      isolation:isolate; min-height:270px;
    }
    .upgrade-card.has-art {
      background-image:linear-gradient(180deg,rgba(4,7,14,.38),rgba(4,7,14,.88)),var(--upgrade-card-art);
      background-position:center,var(--upgrade-card-pos); background-size:cover,400% 100%;
    }
    .upgrade-card.has-art:before {
      content:""; position:absolute; inset:0; z-index:0; pointer-events:none;
      background:linear-gradient(135deg,rgba(255,255,255,.16),transparent 28%,rgba(255,255,255,.06) 55%,transparent 72%);
      opacity:.8; mix-blend-mode:screen;
    }
    .upgrade-card.has-art:after {
      content:""; position:absolute; inset:auto 18px 18px 18px; height:40%;
      background:linear-gradient(180deg,transparent,rgba(0,0,0,.52)); z-index:0; pointer-events:none;
    }
    .upgrade-card.has-art small,
    .upgrade-card.has-art strong,
    .upgrade-card.has-art span:not(.upgrade-icon),
    .upgrade-card.has-art em { position:relative; z-index:1; }
    .upgrade-card .upgrade-icon.art {
      position:relative; z-index:1; display:block; width:58px; height:58px; margin:0 0 16px;
      border-radius:8px; background-image:var(--upgrade-art); background-size:cover; background-position:center;
      box-shadow:inset 0 0 0 1px rgba(255,255,255,.28), 0 14px 30px rgba(0,0,0,.34), 0 0 28px rgba(83,255,181,.14);
    }
    .upgrade-card .rarity-ring {
      position:absolute; inset:10px; border:1px solid rgba(255,255,255,.2); border-radius:8px;
      pointer-events:none; box-shadow:inset 0 0 24px rgba(255,255,255,.05);
    }
    .upgrade-card.rare .rarity-ring { border-color:#6aa9ff; box-shadow:0 0 24px rgba(106,169,255,.32), inset 0 0 24px rgba(106,169,255,.08); }
    .upgrade-card.epic .rarity-ring { border-color:#b044ff; box-shadow:0 0 26px rgba(176,68,255,.34), inset 0 0 24px rgba(176,68,255,.08); }
    .upgrade-card.legendary .rarity-ring { border-color:#ffd166; animation:legendaryPulse 760ms ease-in-out infinite alternate; }
    .upgrade-card .synergy-hint { opacity:.58; transform:translateY(4px); transition:opacity 130ms ease, transform 130ms ease; }
    .upgrade-card:hover .synergy-hint, .upgrade-card:focus-visible .synergy-hint { opacity:1; transform:translateY(0); }
    #rerollUpgrade {
      border-color:rgba(83,255,181,.35); color:#53ffb5; background:rgba(83,255,181,.08);
      min-height:38px; padding:0 14px; border-radius:8px; font-weight:900;
    }
    #rerollUpgrade:disabled { opacity:.42; cursor:not-allowed; }
    @keyframes cardFlip { from { transform:rotateY(90deg); opacity:.25; } to { transform:rotateY(0); opacity:1; } }
    @keyframes legendaryPulse { from { box-shadow:0 0 20px rgba(255,209,102,.28), inset 0 0 18px rgba(255,209,102,.08); } to { box-shadow:0 0 38px rgba(255,209,102,.58), inset 0 0 28px rgba(255,209,102,.16); } }
  `;
  document.head.appendChild(style);
}

function buildHint(choice, state) {
  if (choice.weapon && !state.unlockedWeapons?.has?.(choice.weapon)) return 'Unlocks a new weapon profile.';
  if (choice.stat) return `Stacks with current ${choice.stat} build.`;
  return 'Improves this run immediately.';
}

function escapeHtml(str) {
  return String(str).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  }[char]));
}
